import { graphql, readInlineData } from 'react-relay';

import { defineMessages, IntlShape } from 'dibs-react-intl';
import { sessionStorage } from 'dibs-browser-storage';

const BANNER_CLOSED_MANUALLY = 'bannerClosedManually';

export const isMessageBannerClosedManually = (): boolean =>
    sessionStorage.getItem(BANNER_CLOSED_MANUALLY) as boolean;

export const setIsBannerClosedManuallyInStorage = (): void => {
    sessionStorage.setItem(BANNER_CLOSED_MANUALLY, true);
};

import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { shouldShowExpiringPromo } from './expiringPromoMessageHelpers';

import { MessageBannerCarousel_messages$data } from '../__generated__/MessageBannerCarousel_messages.graphql';
import { sharedMessageHelpers_messages$key } from './__generated__/sharedMessageHelpers_messages.graphql';
import { sharedMessageHelpers_activePromotion$key } from './__generated__/sharedMessageHelpers_activePromotion.graphql';
import { sharedMessageHelpers_expiringPromotion$key } from './__generated__/sharedMessageHelpers_expiringPromotion.graphql';

type Messages = NonNullable<ArrayElement<MessageBannerCarousel_messages$data>>;

export type MessageSet = MessageBannerCarousel_messages$data;

export type Message = NonNullable<ArrayElement<Messages['items']>>;

export type Note = NonNullable<Message['note']>;

export type NoteContent = NonNullable<Note['content']>;

export type NoteObj = Omit<Message, 'impressionCap'>;

export const DEVICE_TYPES = {
    DESKTOP_MOBILE: 'DESKTOP_MOBILE',
    DESKTOP_ONLY: 'DESKTOP_ONLY',
    MOBILE_ONLY: 'MOBILE_ONLY',
} as const;

export const getMessageTitle = (messageItem: Message): string =>
    messageItem?.note?.content?.title || '';

type TrackObjArgs = {
    messageItem: Message;
    action: string;
    label?: string;
};

type TrackObj = {
    category: 'promo interaction';
    action: string;
    label: string;
};

export const getTrackingObj = ({ messageItem, action, label }: TrackObjArgs): TrackObj => {
    return {
        category: 'promo interaction',
        action,
        label: label || getMessageTitle(messageItem),
    };
};

export const isLowResolution = typeof window !== 'undefined' && window.innerWidth < 568; // To match scss mobile breakpoint size.

export const getNoteByDevice = (messageItem: NoteObj | null): Note | null | undefined => {
    const type = messageItem?.note?.content?.type;
    if (isLowResolution && type === DEVICE_TYPES.DESKTOP_ONLY) {
        return null;
    }
    return messageItem?.note;
};

const messagesFragment = graphql`
    fragment sharedMessageHelpers_messages on MessageSetModule @inline {
        items {
            note {
                serviceId
                content {
                    type
                    title
                    message
                    cta {
                        text
                        url
                    }
                }
            }
        }
    }
`;

const activePromotionFragment = graphql`
    fragment sharedMessageHelpers_activePromotion on Promotion @inline {
        promotionMeta {
            customBannerMessage
            desktopBannerMessage
            desktopBannerCta
            mobileBannerMessage
            mobileBannerCta
        }
    }
`;

const expiringPromotionFragment = graphql`
    fragment sharedMessageHelpers_expiringPromotion on Promotion @inline {
        ...expiringPromoMessageHelpers_expiringPromotion
    }
`;

export const hasAnyText = (messageItem: NoteObj): boolean => {
    const noteByDevice = getNoteByDevice(messageItem);
    return !!noteByDevice?.content?.message;
};

export const hasAnyMessagesByDevice = (
    messageSetRef: sharedMessageHelpers_messages$key[] = [],
    activePromotionRef: sharedMessageHelpers_activePromotion$key,
    expiringPromotionRef: sharedMessageHelpers_expiringPromotion$key
): boolean => {
    let hasPromoMessageByDevice = false;
    if (activePromotionRef || expiringPromotionRef) {
        const activePromotionData = readInlineData(activePromotionFragment, activePromotionRef);
        const expiringPromotionData = readInlineData(
            expiringPromotionFragment,
            expiringPromotionRef
        );
        const { desktopBannerMessage, mobileBannerMessage } =
            activePromotionData?.promotionMeta || {};
        hasPromoMessageByDevice =
            // `shouldShowExpiringPromo(userData)` seems like it will always return false
            // b/c it only looks for `expiringPromotion` and this query only has `activePromotion`
            shouldShowExpiringPromo(expiringPromotionData) ||
            (isLowResolution ? !!mobileBannerMessage : !!desktopBannerMessage);
    }
    return (
        hasPromoMessageByDevice ||
        messageSetRef.some(messageSetRefItem => {
            const { items } = readInlineData(messagesFragment, messageSetRefItem);
            return (items || []).filter(filterNulls).some(messageItem => hasAnyText(messageItem));
        })
    );
};

const fallbackText = defineMessages({
    fallback: {
        id: 'dbl.Header.MessageBanner.global_fallback_text',
        defaultMessage: "1stDibs is the world's leading marketplace for beautiful and rare objects",
    },
});

const globalFallbackMessage = (intl: IntlShape): NoteObj => ({
    note: {
        serviceId: 'global_fallback_template',
        content: {
            title: 'Global Fallback',
            message: intl.formatMessage(fallbackText.fallback),
            cta: null,
            type: DEVICE_TYPES.DESKTOP_MOBILE,
        },
    },
});

export const getFallbackMessagesArray = (intl: IntlShape, messageSet: MessageSet): NoteObj[] => {
    const fallback = (messageSet?.[2]?.items || [])
        .filter(filterNulls)
        .filter(item => hasAnyText(item));
    if (!fallback.length) {
        return [globalFallbackMessage(intl)];
    }
    return fallback;
};
